﻿import { FC } from 'react';
import { Container, Section, Card, CardTitle, CardDescription, CardFooter, Button, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { ROUTES } from '../config/routes';
import { getPaidProducts, formatPriceINR } from '../config/catalog';
import { trackProductView, trackBuyClick } from '../analytics/events';
import { PageProps } from './HomePage';

export const DigitalProductsPage: FC<PageProps> = ({ onNavigate }) => {
  const products = getPaidProducts();

  const handleSelectProduct = (slug: string, price: number, routePath: string) => {
    trackProductView(slug, price, 'INR');
    trackBuyClick(slug, price);
    onNavigate(routePath);
  };

  return (
    <>
      <SEOHead
        title="Career Toolkits & Digital Accelerators"
        description="Immediate-access, field-tested career toolkits. Master ATS resumes, optimize your LinkedIn profile, prepare for interviews, and utilize AI job search tools."
      />

      {/* Hero */}
      <section style={{ backgroundColor: '#ffffff', borderBottom: '1px solid var(--color-border-subtle)', padding: '4.5rem 0' }}>
        <Container size="xl">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <Badge variant="primary" style={{ marginBottom: '1rem' }}>Instant Download Toolkits</Badge>
            <h1 style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3rem)', fontWeight: 800, color: 'var(--color-navy-dark)', lineHeight: 1.2 }}>
              Practical Career Tools You Can Use Today
            </h1>
            <p style={{ fontSize: '1.15rem', color: 'var(--color-text-secondary)', marginTop: '1.25rem', lineHeight: 1.65 }}>
              Engineered, self-paced career acceleration toolkits. Ready-to-use templates, structured frameworks, and actionable roadmaps built to elevate your hiring outcomes.
            </p>
          </div>
        </Container>
      </section>

      {/* Product Catalog Grid */}
      <Section title="Explore Digital Career Toolkits" subtitle="Select any toolkit below to examine complete modules, preview contents, and review FAQs.">
        <div className="cs-grid-3">
          {products.map(product => (
            <Card
              key={product.id}
              className="cs-card-hover"
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: '2rem'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <Badge variant="neutral">{product.deliverableType.replace('_', ' ')}</Badge>
                <div style={{ fontSize: '1.45rem', fontWeight: 800, color: 'var(--color-navy-dark)' }}>
                  {formatPriceINR(product.priceINR)}
                </div>
              </div>

              <CardTitle style={{ fontSize: '1.25rem', minHeight: '3.2rem' }}>
                {product.title}
              </CardTitle>

              <CardDescription style={{ marginTop: '0.5rem', lineHeight: 1.6, flex: '1 0 auto' }}>
                {product.subtitle}
              </CardDescription>

              <div style={{ marginTop: '1.5rem', padding: '0.85rem', backgroundColor: 'var(--color-bg-canvas)', borderRadius: 'var(--radius-md)' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-navy-dark)', textTransform: 'uppercase' }}>
                  Target Audience:
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem', lineHeight: 1.45 }}>
                  {product.targetAudience.join(', ')}
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-navy-dark)', textTransform: 'uppercase' }}>
                  Format:
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.2rem' }}>
                  {product.files.map(f => f.fileFormat).join(' • ')} (Instant Download)
                </div>
              </div>

              <CardFooter style={{ marginTop: '1.75rem', paddingTop: '1.25rem' }}>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => handleSelectProduct(product.slug, product.priceINR, product.routePath)}
                  style={{ width: '100%' }}
                >
                  View Toolkit Details →
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </Section>

      {/* Free Resource Banner */}
      <section style={{ backgroundColor: 'var(--color-navy-dark)', color: '#ffffff', padding: '4rem 0' }}>
        <Container size="lg">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: '720px', margin: '0 auto' }}>
            <Badge variant="teal" style={{ marginBottom: '1rem' }}>Free Lead Magnet</Badge>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#ffffff' }}>
              Looking for a Complimentary Starter Resource?
            </h2>
            <p style={{ fontSize: '1.05rem', color: '#cbd5e1', marginTop: '1rem', lineHeight: 1.65 }}>
              Download the <strong>Interview Preparation Starter Guide</strong> at zero cost. Get an essential checklist and foundational STAR response guide before taking interviews.
            </p>
            <div style={{ marginTop: '1.75rem' }}>
              <Button
                variant="teal"
                size="lg"
                onClick={() => onNavigate(ROUTES.FREE_INTERVIEW_GUIDE)}
              >
                Get the Free Starter Guide
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};


